import axios from 'axios';
import { getJson, sendJson } from '@/lib/api';
import { authHeader } from '@/lib/events';

/** Charity directory statuses. */
export const CHARITY_STATUS = { approved: 'approved', pending: 'pending', doNotUse: 'do_not_use' } as const;
export type CharityStatus = (typeof CHARITY_STATUS)[keyof typeof CHARITY_STATUS];
export const CHARITY_STATUS_LABELS: Readonly<Record<CharityStatus, string>> = { approved: 'Approved', pending: 'Pending', do_not_use: 'Do not use' };

/** What happened with a charity on an event. */
export const CHARITY_OUTCOME = { pending: 'pending', accepted: 'accepted', declined: 'declined', noResponse: 'no_response', clientChose: 'client_chose' } as const;
export type CharityOutcome = (typeof CHARITY_OUTCOME)[keyof typeof CHARITY_OUTCOME];
export const CHARITY_OUTCOME_LABELS: Readonly<Record<CharityOutcome, string>> = {
  pending: 'Pending',
  accepted: 'Accepted',
  declined: 'Declined',
  no_response: 'No response',
  client_chose: 'Client chose',
};

export interface CharityContact {
  readonly name?: string;
  readonly role?: string;
  readonly email?: string;
  readonly phone?: string;
  readonly chapter?: string;
}

export interface Charity {
  readonly _id: string;
  readonly name: string;
  readonly ein?: string;
  readonly website?: string;
  /** From Salesforce. */
  readonly phone?: string;
  /** From Salesforce (the Account's billing address). */
  readonly address?: { readonly street?: string; readonly city?: string; readonly state?: string; readonly postalCode?: string };
  readonly causeTags: readonly string[];
  readonly regions: ReadonlyArray<{ readonly city?: string; readonly state?: string }>;
  readonly contacts: readonly CharityContact[];
  readonly status: CharityStatus;
  readonly statusNote?: string;
  readonly salesforceAccountId?: string;
  /** When Salesforce last changed this row. */
  readonly syncedAt?: string;
  readonly history: ReadonlyArray<{ readonly eventId: string; readonly outcome: CharityOutcome; readonly at: string }>;
  readonly updatedAt?: string;
}

export type CharityWrite = Omit<Charity, '_id' | 'history' | 'updatedAt' | 'syncedAt' | 'phone' | 'address'>;

/** One event a charity was on, newest first. */
export interface CharityEvent {
  readonly eventId: string;
  readonly contractNumber: string | null;
  readonly accountName: string | null;
  readonly programName: string | null;
  readonly eventDate: string | null;
  readonly city: string | null;
  readonly state: string | null;
  readonly stageName: string | null;
  /** The charity plan's outcome, when the event's plan picked this charity. */
  readonly outcome: CharityOutcome | null;
}

/** What a Salesforce sync did. */
export interface CharitySyncSummary {
  readonly fetched: number;
  readonly inserted: number;
  readonly linked: number;
  readonly updated: number;
  readonly unchanged: number;
  readonly skippedRemoved: number;
}

/** Roles that may pull charities from Salesforce on demand (the API allows admins only). */
export const CHARITY_SYNC_ROLES: readonly string[] = ['admin', 'superadmin'];

/** Whether a staff role may run the Salesforce sync. */
export function canSyncCharities(role: string | undefined): boolean {
  return role !== undefined && CHARITY_SYNC_ROLES.includes(role);
}

/** "12 new, 3 updated" — what a sync changed, in a sentence fragment. */
export function charitySyncSummaryText(summary: CharitySyncSummary): string {
  const parts = [
    summary.inserted ? `${summary.inserted} new` : null,
    summary.linked ? `${summary.linked} matched to Salesforce` : null,
    summary.updated ? `${summary.updated} updated` : null,
  ].filter(Boolean);
  return parts.length ? parts.join(', ') : 'Already up to date';
}

/** The event's charity plan. */
export interface CharityPlan {
  readonly charityId?: string;
  readonly outcome?: CharityOutcome;
  readonly speaker?: { readonly name?: string; readonly cell?: string };
  readonly arrivalAt?: string;
  readonly pickupPlan?: string;
  readonly talkingPoints?: string;
  readonly note?: string;
  readonly updatedAt?: string;
}

export async function listCharities(params: { status?: CharityStatus; search?: string } = {}, signal?: AbortSignal): Promise<readonly Charity[]> {
  return getJson<Charity[]>('/charities', params, signal);
}
export async function readCharity(id: string, signal?: AbortSignal): Promise<Charity> {
  return getJson<Charity>(`/charities/${id}`, undefined, signal);
}
export async function listCharityEvents(id: string, signal?: AbortSignal): Promise<readonly CharityEvent[]> {
  return getJson<CharityEvent[]>(`/charities/${id}/events`, undefined, signal);
}
export async function syncCharities(): Promise<CharitySyncSummary> {
  return sendJson<CharitySyncSummary>('post', '/charities/sync');
}
export async function createCharity(body: CharityWrite): Promise<Charity> {
  return sendJson<Charity>('post', '/charities', body);
}
export async function updateCharity(id: string, body: Partial<CharityWrite>): Promise<Charity> {
  return sendJson<Charity>('patch', `/charities/${id}`, body);
}
export async function deleteCharity(id: string): Promise<void> {
  await sendJson('delete', `/charities/${id}`);
}

/** Charities ranked for an event's picker: approved first, nearest first. */
export async function listCharityCandidates(eventId: string, signal?: AbortSignal): Promise<readonly Charity[]> {
  const response = await axios.get<{ data: Charity[] }>(`/api/v3/events/${eventId}/charity-candidates`, { ...(signal ? { signal } : {}), headers: await authHeader() });
  return response.data.data;
}

/** Saves the plan; a null charityId clears it. */
export async function saveCharityPlan(eventId: string, plan: Omit<CharityPlan, 'updatedAt' | 'charityId'> & { charityId: string | null }): Promise<void> {
  await axios.put(`/api/v3/events/${eventId}/charity-plan`, plan, { headers: await authHeader() });
}

/** A status pill tone. */
export function charityStatusTone(status: CharityStatus): 'success' | 'warning' | 'danger' {
  return status === 'approved' ? 'success' : status === 'pending' ? 'warning' : 'danger';
}

/** An outcome pill tone. */
export function charityOutcomeTone(outcome: CharityOutcome): 'success' | 'warning' | 'danger' | 'neutral' {
  if (outcome === 'accepted' || outcome === 'client_chose') return 'success';
  if (outcome === 'pending') return 'warning';
  if (outcome === 'declined' || outcome === 'no_response') return 'danger';
  return 'neutral';
}

/** "Austin, TX · Dallas, TX". */
export function regionsText(charity: Pick<Charity, 'regions'>): string {
  return charity.regions.map((region) => [region.city, region.state].filter(Boolean).join(', ')).filter(Boolean).join(' · ');
}
